import React, {useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import AccountingSettingsTab from './accounting-settings-tab'
import Page from "../../../common/components/layout/layout-components/page";
import PageHeader from "../../../common/components/layout/layout-components/page/page-header";
import PageHeaderInfo from "../../../common/components/layout/layout-components/page/page-header-info";
import NavResponsive from "../../../common/components/nav-responsive";
import DocumentTypesTab from "../../../common/components/tabs/document-types-tab";
import Layout from "../../../common/components/layout";
import PayrollSettingsTab from "./payroll-settings-tab";
import {checkPerm, getSystemName} from "../../../common/util/util-helpers";
import {READ_PERM} from "../../../util/util-constants";
import {ACCOUNTING_SETTINGS_INCLUDED_LOOKUPS, UPDATE_PERM} from "../../../common/util/util-consts";
import WorkOrderTab from "./settings-work-order-tab";
import {getUser} from "../../../common/util/util-auth";
import Lookups from "../../general/settings/lookupTab";
import ModalConfirm from "../../../common/components/modal/modal-confirm";
import BillingTab from "../../general/settings/billing-tab";

export default function AccountingSettingsView({translate, match, history, location}) {
    const dispatch = useDispatch()
    const ui = useSelector((state) => state.ui)
    const user = useSelector((state) => state.user)
    const resource = useSelector((state) => state.resource)
    const thirdResource = useSelector((state) => state.thirdResource)

    const [tabs, setTabs] = useState(() => getTabs());
    const currentTab = tabs.find(tab => tab.current);

    const [breakpoint, setBreakpoint] = useState({});
    const [isTabDirty, setIsTabDirty] = useState(false);
    const [isDirtyStateWarningModalOpen, setIsDirtyStateWarningModalOpen] = useState(false);
    const [tabClicked, setTabClicked] = useState("");

    const handleTabChange = (resource) => {
        if (currentTab.resource === resource) {
            return false;
        }

        if (isTabDirty) {
            setIsDirtyStateWarningModalOpen(true);
            setTabClicked(resource)
        } else {
            setTabs(
                tabs.map((it) => {
                    it.current = it.resource === resource;
                    return it
                })
            )
        }
    }

    const handleBreakpointChange = (breakpoint) => {
        setBreakpoint(breakpoint)
    }

    return (
        <Layout
            ui={ui}
            user={user}
            match={match}
            history={history}
            location={location}
            translate={translate}
            dispatch={dispatch}
            hasFooter={currentTab.name === 'AccountingSettings' || currentTab.name === 'billingTab'}
            isAccessible={!(resource.errorStatus === 2)}
            isDirty={isTabDirty}
            onBreakpoinChange={handleBreakpointChange}
        >
            <Page>
                <PageHeader
                    title={translate('page.heading.AccountingSettings')}
                    afterTitle={(
                        <PageHeaderInfo
                            dispatch={dispatch}
                        />
                    )}
                />

                <NavResponsive
                    tabs={tabs}
                    onTabChange={handleTabChange}
                    translate={translate}
                />

                {currentTab.name === 'AccountingSettings' && (
                    <AccountingSettingsTab
                        dispatch={dispatch}
                        resource={resource}
                        setIsTabDirty={setIsTabDirty}
                        translate={translate}
                    />
                )}

                {currentTab.name === 'billingTab' && (
                        <BillingTab
                            translate={translate}
                            setIsTabDirty={setIsTabDirty}
                            resourcePath={currentTab.resource}
                        />
                )}

                {currentTab.name === 'WorkOrders' && (
                    <WorkOrderTab
                        resourcePath={currentTab.resource}
                        setIsTabDirty={setIsTabDirty}
                        translate={translate}
                    />
                )}

                {currentTab.name === 'customFields' && (
                    <Lookups
                        customLookupFilter={(item, name) => {
                            if (name === "DocumentType") {
                                return !!item.IsAccountingDocument
                            }
                            return true
                        }}
                        includeLookups={ACCOUNTING_SETTINGS_INCLUDED_LOOKUPS}
                        translate={translate}
                        resource={resource}
                        dispatch={dispatch}
                    />
                )}

                {currentTab.name === 'PayrollSettings' && (
                    <PayrollSettingsTab
                        translate={translate}
                        dispatch={dispatch}
                        resource={resource}
                        location={location}
                        setIsTabDirty={setIsTabDirty}
                        thirdResource={thirdResource}
                    />
                )}

                {currentTab.name === 'DocumentTypes' && (
                    <DocumentTypesTab
                        translate={translate}
                        primaryType={'IsAccountingDocument'}
                        breakpoint={breakpoint}
                    />
                )}
            </Page>

            <ModalConfirm
                show={isDirtyStateWarningModalOpen}
                type="default"
                onClose={() => setIsDirtyStateWarningModalOpen(false)}
                onConfirm={() => {
                    setTabs(
                        tabs.map((it) => {
                            it.current = it.resource === tabClicked;
                            return it
                        })
                    );
                    setIsDirtyStateWarningModalOpen(false);
                    setIsTabDirty(false);
                }}
                closeButtonLabel={translate("btn.stay_on_current_tab")}
                buttonLabel={translate("text.Continue")}
                title={translate('dialog_heading.unsaved_changes_on_tab')}
                translate={translate}
            >
                <p className="text-base">{translate('text.changes_on_tab')}</p>
                <p className="text-base">{translate('text.changes_will_be_lost')}</p>
            </ModalConfirm>
        </Layout>
    )
}

const getTabs = () => {
    return [
        {
            name: 'AccountingSettings',
            resource: Resources.AccountingSettings,
            current: true,
            visible: true
        },
        {
            name: 'billingTab',
            resource: Resources.CompanyBilling,
            current: false,
            visible: true
        },
        {
            name: 'WorkOrders',
            resource: 'work-order-settings',
            current: false,
            visible: checkPerm(Resources.AccountingSettings, READ_PERM) && !!getUser('Company.IsWorkOrdersON', false)
        },
        {
            name: 'customFields',
            resource: Resources.Lookup,
            current: false,
            visible: true
        },
        {
            name: 'PayrollSettings',
            resource: Resources.PayrollSettingsGeneral,
            current: false,
            hasPerm: checkPerm(Resources.PayrollSettingsGeneral, UPDATE_PERM) && checkPerm(Resources.PayrollSettings, UPDATE_PERM),
            visible: getSystemName() === 'main'
        },
        {
            name: 'DocumentTypes',
            resource: Resources.DocumentTypes,
            current: false,
            visible: true
        }
    ]
}
